import React from "react";
import { Box, TableCell, IconButton } from "@mui/material";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import clsx from "clsx";
import makeStyles from "@mui/styles/makeStyles";

import { httpCallables } from "../../firebase";
import { TooltipWithIntl } from "../SharedComponents";
import { captureException } from "../../utils/errorHandlers";
import { addSnackbar, clearSnackbars } from "../../redux/snackbarSlice";
import { selectTasks } from "../../redux/tasksSlice";
import { selectTexts, setTexts } from "../../redux/textsSlice";

// Constants
const BUTTON_CONTAINER_WIDTH = "100px";
const MAX_AUTHOR_WIDTH = "200px";
const TEXT_LINE_CLAMP = 2;

// Styles
const useStyles = makeStyles((theme) => ({
  link: {
    color: theme.palette.text.primary
  },
  authorName: {
    whiteSpace: "nowrap",
    maxWidth: MAX_AUTHOR_WIDTH,
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  textName: {
    display: "-webkit-box",
    "-webkit-line-clamp": TEXT_LINE_CLAMP,
    textOverflow: "ellipsis",
    "-webkit-box-orient": "vertical",
    overflow: "hidden"
  }
}));

// Custom hooks
const useTextData = (selectedCourseId, text) => {
  const tasks = useSelector((state) =>
    selectTasks(state, selectedCourseId?.toString())
  );
  const texts = useSelector((state) => selectTexts(state, selectedCourseId));

  const textHasTasks = Boolean(
    tasks.filter((task) => task.text_id === text.id).length
  );

  return { tasks, texts, textHasTasks };
};

const useDeleteText = () => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const deleteText = async (text, selectedCourseId, texts) => {
    try {
      // Show loading state
      dispatch(
        addSnackbar({
          message: intl.formatMessage({
            id: "comments.onDelete",
            defaultMessage: "Deleting..."
          })
        })
      );

      // Optimistically update UI
      const filteredTexts = texts.filter((t) => t.id !== text.id);
      dispatch(setTexts(filteredTexts));

      // Delete from backend
      const deleteData = {
        text_id: text.id,
        course_id: selectedCourseId,
        file_url: text.file_url
      };

      await httpCallables.deleteCourseText(deleteData);

      // Show success with undo option
      dispatch(clearSnackbars());
      dispatch(
        addSnackbar({
          message: intl.formatMessage({
            id: "text.deleted.success",
            defaultMessage: "Text deleted"
          }),
          actions: [
            {
              intlId: "undo",
              intlDefaultMsg: "Undo",
              callBack: "undoDeleteText"
            }
          ],
          data: { textToDelete: text }
        })
      );
    } catch (error) {
      // Revert UI changes and show error
      dispatch(setTexts(texts));

      const errorMessage = intl.formatMessage({
        id: "error.deletingFailed",
        defaultMessage: `Failed to delete text ${text.name}`
      });

      captureException(error, errorMessage);
      dispatch(
        addSnackbar({
          message: errorMessage,
          severity: "error"
        })
      );
    }
  };

  return deleteText;
};

// Sub-components
const AuthorCell = ({ author, className, classes }) => (
  <TableCell className={clsx(classes.cell, className)} align="left">
    <span className={clsx(classes.link, classes.authorName)}>{author}</span>
  </TableCell>
);

const TextNameCell = ({ name, className, classes }) => (
  <TableCell className={clsx(classes.cell, className)} align="left">
    <span className={clsx(classes.link, classes.textName)}>{name}</span>
  </TableCell>
);

const EditButton = ({ text, setOpenTexts, setSelectedText }) => {
  const handleEdit = (e) => {
    e.stopPropagation();
    setOpenTexts("Edit");
    setSelectedText(text);
  };

  return (
    <TooltipWithIntl
      intlStringId="text.edit.msg"
      defaultMessage="Edit text"
      placement="top">
      <IconButton
        aria-label="edit text"
        size="large"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={handleEdit}>
        <EditOutlinedIcon />
      </IconButton>
    </TooltipWithIntl>
  );
};

const DeleteButton = ({ text, textHasTasks, onDelete }) => {
  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!textHasTasks) {
      await onDelete(text);
    }
  };

  const tooltipMessage = textHasTasks
    ? "Remove associated tasks before deleting the text"
    : "Delete text";

  return (
    <TooltipWithIntl
      intlStringId="text.delete.msg"
      defaultMessage={tooltipMessage}
      placement={textHasTasks ? "bottom" : "top"}>
      <IconButton
        disabled={textHasTasks}
        size="large"
        aria-label="delete text"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={handleDelete}>
        <DeleteOutlineOutlinedIcon />
      </IconButton>
    </TooltipWithIntl>
  );
};

const ActionsCell = ({
  text,
  hovered,
  textHasTasks,
  setOpenTexts,
  setSelectedText,
  onDelete
}) => {
  const handleCellClick = (e) => {
    e.stopPropagation();
  };

  return (
    <TableCell onClick={handleCellClick} align="center">
      <Box
        sx={{
          display: "flex",
          flexFlow: "row",
          width: BUTTON_CONTAINER_WIDTH,
          opacity: hovered ? 1 : 0,
          "&:focus, &:focus-within": {
            opacity: 1
          }
        }}>
        <EditButton
          text={text}
          setOpenTexts={setOpenTexts}
          setSelectedText={setSelectedText}
        />
        <DeleteButton
          text={text}
          textHasTasks={textHasTasks}
          onDelete={onDelete}
        />
      </Box>
    </TableCell>
  );
};

// Main component
export const TextColumns = ({
  text,
  hovered,
  isTeacher,
  setOpenTexts,
  setSelectedText,
  className
}) => {
  const classes = useStyles();
  const selectedCourseId = useSelector(
    (state) => state.user.userProfile?.selectedCourseId
  );

  const { texts, textHasTasks } = useTextData(selectedCourseId, text);
  const deleteText = useDeleteText();

  const handleDelete = async (textToDelete) => {
    await deleteText(textToDelete, selectedCourseId, texts);
  };

  return (
    <>
      <AuthorCell
        author={text.author}
        className={className}
        classes={classes}
      />

      <TextNameCell name={text.name} className={className} classes={classes} />

      {isTeacher && (
        <ActionsCell
          text={text}
          hovered={hovered}
          textHasTasks={textHasTasks}
          setOpenTexts={setOpenTexts}
          setSelectedText={setSelectedText}
          onDelete={handleDelete}
        />
      )}
    </>
  );
};
